import { FormEvent, useState } from 'react'
import { Search, UserCheck, User, ShieldCheck } from 'lucide-react'
import { patientsApi, type MatchCandidate } from '../lib/api'
import { VERIFICATION_TIERS } from '../lib/verificationTiers'
import { readOfflineRegistrations } from '../lib/offlineStore'
import type { ProviderRosterRow } from '../api/provider'
import { patientResponseToRosterRow, SAMPLE_ROSTER } from '../lib/samples/roster'

/**
 * Patient search interface (Sprint 2 — Minimum Registry).
 *
 * Searches by name (Arabic/Latin) + DOB via POST /api/v1/patients/search and
 * renders candidate matches with their MPI confidence score and a
 * verification-tier badge derived from the score band.
 *
 * Offline fallback (static Cloudflare Pages demo): on any ApiError, the same
 * query runs in-memory over the bundled sample roster plus locally persisted
 * offline registrations (name substring + DOB prefix). Every fallback result
 * is labelled match_reason='demo sample' and the panel carries the
 * "Sample data" badge (prov.sampleData) so demo matches are never mistaken
 * for live MPI hits.
 */

import { useLang, t, fmt, matchReasonLabel } from '../lib/i18n'

/**
 * In-memory search over the bundled roster — mirrors the request shape of
 * POST /patients/search: case-insensitive substring match on name_latin /
 * name_ar plus a DOB prefix match (roster DOB starts with the query DOB).
 */
function scoreCandidate(
  row: ProviderRosterRow,
  query: { nameAr?: string; nameLatin?: string; dob?: string },
): MatchCandidate | null {
  const reasons: string[] = []
  let score = 0

  const qLatin = query.nameLatin?.trim().toLowerCase() ?? ''
  if (qLatin && row.name_latin) {
    const latin = row.name_latin.toLowerCase()
    if (latin.includes(qLatin) || qLatin.includes(latin)) {
      score = Math.max(score, 0.95)
      reasons.push('name_latin')
    }
  }

  const qArabic = query.nameAr?.trim() ?? ''
  if (qArabic && row.name_ar) {
    if (row.name_ar.includes(qArabic) || qArabic.includes(row.name_ar)) {
      score = Math.max(score, 0.9)
      reasons.push('name_ar')
    }
  }

  if (query.dob && row.dob && row.dob.startsWith(query.dob)) {
    score = Math.max(score, 0.85)
    reasons.push('dob')
  }

  if (reasons.length === 0) return null
  return {
    person_id: row.person_id,
    patient_id: row.patient_id,
    nhi: row.nhi,
    name_ar: row.name_ar,
    name_latin: row.name_latin,
    dob: row.dob,
    score,
    match_reason: 'demo sample',
  }
}

function tierFromScore(score: number): { level: number; label: string; tone: string } {
  if (score >= 0.95) return { level: 3, label: t('search.strong'), tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' }
  if (score >= 0.7) return { level: 2, label: t('search.review2'), tone: 'bg-amber-50 text-amber-800 border-amber-200' }
  return { level: 1, label: t('search.weak'), tone: 'bg-stone-100 text-stone-600 border-stone-200' }
}

function scorePct(score: number): string {
  return `${Math.round(score * 100)}%`
}

export function PatientSearch({
  onSelect,
}: {
  onSelect: (candidate: MatchCandidate) => void
}) {
  const [nameAr, setNameAr] = useState('')
  const [nameLatin, setNameLatin] = useState('')
  const [dob, setDob] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<MatchCandidate[]>([])
  const [searched, setSearched] = useState(false)
  const [offline, setOffline] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    if (!nameAr.trim() && !nameLatin.trim() && !dob) {
      setError(t('search.needInput'))
      return
    }
    setSearching(true)
    const query = {
      nameAr: nameAr.trim() || undefined,
      nameLatin: nameLatin.trim() || undefined,
      dob: dob || undefined,
    }
    try {
      const res = await patientsApi.search({
        name_ar: query.nameAr,
        name_latin: query.nameLatin,
        dob: query.dob,
      })
      setResults(res.candidates)
      setOffline(false)
      setSearched(true)
    } catch (err) {
      // Static-site fallback: score the query against the bundled roster +
      // offline registrations in-memory (badged) instead of a dead-end error.
      // Any ApiError (fetch TypeError = network down, or a non-2xx status —
      // the static host answers 404/405/501 to API routes) routes here.
      const isApiError = err instanceof Error && err.name === 'ApiError'
      if (err instanceof TypeError || isApiError) {
        const pool = [
          ...readOfflineRegistrations().map((entry) => patientResponseToRosterRow(entry.patient)),
          ...SAMPLE_ROSTER,
        ]
        const candidates = pool
          .map((row) => scoreCandidate(row, query))
          .filter((candidate): candidate is MatchCandidate => candidate !== null)
          .sort((a, b) => b.score - a.score)
        setResults(candidates)
        setOffline(true)
      } else {
        setError(err instanceof Error ? err.message : t('search.failed'))
        setResults([])
      }
      setSearched(true)
    } finally {
      setSearching(false)
    }
  }

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
      <div className="mb-4 flex items-center gap-2">
        <Search className="h-5 w-5 text-primary-700" />
        <h3 className="text-lg font-semibold text-stone-950">{t('search.patients')}</h3>
      </div>

      <form onSubmit={submit} className="grid gap-3 md:grid-cols-3">
        <label className="grid gap-1.5 text-sm font-medium text-stone-700">
          {t('search.arabicName')}
          <input
            value={nameAr}
            onChange={(event) => setNameAr(event.target.value)}
            placeholder={t('search.exampleAr')}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
        <label className="grid gap-1.5 text-sm font-medium text-stone-700">
          {t('search.latinName')}
          <input
            value={nameLatin}
            onChange={(event) => setNameLatin(event.target.value)}
            placeholder={t('search.exampleLatin')}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
        <label className="grid gap-1.5 text-sm font-medium text-stone-700">
          {t('search.dob')}
          <input
            type="date"
            value={dob}
            onChange={(event) => setDob(event.target.value)}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
        <div className="md:col-span-3">
          <button
            type="submit"
            disabled={searching}
            className="flex h-11 items-center justify-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95 disabled:opacity-60"
          >
            <Search className="h-4 w-4" />
            {searching ? t('search.searching') : t('search.searchNow')}
          </button>
        </div>
      </form>

      {error && (
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800" role="alert">
          {error}
        </p>
      )}

      {searched && !searching && (
        <div className="mt-5">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-stone-500">
            {offline && (
              <span
                className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
                data-testid="sample-data-badge"
              >
                {t('prov.sampleData')}
              </span>
            )}
            {results.length === 0
              ? t('search.none')
              : fmt(t('search.candidates'), { n: results.length, s: results.length === 1 ? '' : 's' })}
          </div>
          {results.length > 0 && (
            <ul className="divide-y divide-stone-100 overflow-hidden rounded-lg border border-stone-200">
              {results.map((candidate) => {
                const tier = tierFromScore(candidate.score)
                return (
                  <li key={candidate.person_id} className="flex items-center gap-4 p-4 transition hover:bg-emerald-50/50">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-stone-100 text-stone-600">
                      <User className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-stone-900">
                        {candidate.name_latin || candidate.name_ar || t('search.unnamed')}
                      </div>
                      <div className="text-xs text-stone-500">
                        {candidate.name_ar && candidate.name_latin ? `${candidate.name_ar} · ` : ''}
                        {candidate.dob ? fmt(t('search.dobValue'), { dob: candidate.dob }) : ''}
                        {fmt(t('search.nhi'), { nhi: candidate.nhi })}
                      </div>
                      <div className="mt-0.5 text-xs text-stone-400">{matchReasonLabel(candidate.match_reason)}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-sm font-semibold text-stone-700">{scorePct(candidate.score)}</span>
                      <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold ${tier.tone}`}>
                        {tier.level >= 3 ? <ShieldCheck className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
                        {tier.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => onSelect(candidate)}
                        className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 transition hover:border-emerald-300 hover:text-emerald-800 active:scale-95"
                      >
                        {t('search.view')}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}
